import { AfterViewInit, Component, OnInit, Optional, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { GeoAddress } from '../../models/common/geo-address.model';
import { GeoElement } from '../../models/common/geo-element.model';
import { LatLng } from '../../models/common/lat-lng';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { SessionStorageService } from '../../services/session-storage.service';
import { CompanyService } from '../../company/company.service';
import { CustomerService } from '../../customer/customer.service';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { GoogleMapsLibrariesLoaderService } from '../../services/google/google-libraries-loader.service';
import { Location, LocationMerchandiserAssignment, LocationRetailer } from '../location.model';
import { LocationService } from '../location.service';
import { Customer } from '../../customer/customer.model';
import { Company } from '../../company/company.model';
import { Country } from '../../models/common/country.model';
import { City } from '../../models/common/city.model';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { CrmAccount, CrmClient, CrmMerchandiser } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { TextareaModule } from 'primeng/textarea';
import { MessageModule } from 'primeng/message';
import { ProgressBarModule } from "primeng/progressbar";
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { CustomerFormComponent } from 'src/app/customer/customer-form/customer-form.component';
import { CompanyFormComponent } from 'src/app/company/company-form/company-form.component';
import { HttpClient } from '@angular/common/http';
import { LocationSearchComponent } from 'src/app/shared/location-search/location-search.component';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from 'src/app/shared/page-layout/page-form-section.component';
import { MerchandiserFormComponent } from 'src/app/crm/merchandisers/merchandiser-form.component';
import { TransportPlanningService } from 'src/app/transport-planning/transport-planning.service';
import { LocationZone } from 'src/app/transport-planning/transport-planning.model';

type CityOption = City & {
    searchText?: string;
};

type LocationCreationSourceContext = 'WAREHOUSE' | 'POSM_STORE' | 'GENERAL';

@Component({
    selector: 'app-create-location',
    templateUrl: './create-location.component.html',
    styleUrls: ['./create-location.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        RouterModule,
        ToastModule,
        InputTextModule,
        ButtonModule,
        SelectModule,
        MultiSelectModule,
        BreadcrumbModule,
        DialogModule,
        TextareaModule,
        MessageModule,
        ProgressBarModule,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        CustomerFormComponent,
        CompanyFormComponent,
        MerchandiserFormComponent,
        LocationSearchComponent,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent
    ],
    providers: [
        MessageService,
        LocationService,
        SessionStorageService,
        CompanyService,
        CustomerService,
        GoogleMapsLibrariesLoaderService,
        BaseMasterDataService
    ]
})
export class CreateLocationComponent implements OnInit, AfterViewInit {

    @ViewChild(LocationSearchComponent) private locationSearchComponent?: LocationSearchComponent;

    canCreateLocation = true;
    canEditLocation = true;
    canManageLocation = true;

    googleMap: any;
    createEditLocationForm: FormGroup<any>;
    createContactForm: FormGroup<any>;
    searchLocation: FormControl;
    searchDefaultContact: FormControl;
    contactsDropdown: FormControl;
    companyDropdown: FormControl;
    countryDropdown: FormControl;
    cityDropdown: FormControl;
    locationTypeDropdown: FormControl;
    locationZoneDropdown: FormControl;
    externalLocationReferenceInput: FormControl;
    nameInput: FormControl;
    googleMapsUrl: FormControl;


    searchPlaceResults: any[];
    locationId: number;
    isLoading: boolean = false;
    createEditLocation: Location = new Location();
    customerList: Customer[];
    searchContactResults: Customer[];
    merchandiserList: CrmMerchandiser[] = [];
    crmClientList: CrmClient[] = [];
    crmAccountList: CrmAccount[] = [];
    companyList: Company[];
    retailerList: LocationRetailer[] = [];
    countryList: Country[] = [];
    cityList: City[] = [];
    filteredCityList: City[] = [];
    locationZones: LocationZone[] = [];
    locationTypes = [
        { name: 'Customer Site', value: 'CUSTOMER' },
        { name: 'Warehouse', value: 'WAREHOUSE' },
        { name: 'Distribution Center', value: 'DISTRIBUTION_CENTER' },
        { name: 'Cross Dock', value: 'CROSS_DOCK' },
        { name: 'POSM Store', value: 'POSM_STORE' },
        { name: 'Retail Store', value: 'STORE' },
        { name: 'Office', value: 'OFFICE' },
        { name: 'Branch', value: 'BRANCH' },
        { name: 'Depot', value: 'DEPOT' },
        { name: 'Yard', value: 'YARD' },
        { name: 'Hub', value: 'HUB' },
        { name: 'Factory / Plant', value: 'FACTORY' },
        { name: 'Supplier / Vendor Site', value: 'SUPPLIER' },
        { name: 'Port / Terminal', value: 'PORT' },
        { name: 'Service Center', value: 'SERVICE_CENTER' },
        { name: 'Temporary Site', value: 'TEMPORARY_SITE' }
    ];
    locationCreationInProgress: boolean = false;
    errorMessages: string = '';
    errorMessageList: string[] = [];

    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;
    companyFormHeader: string;
    companyFormSubmitLabel: string;
    selectedCustomer: Customer = new Customer();
    selectedCompany: Company = new Company();
    showCompanyForm: boolean = false;
    customerFormHeader: string;
    customerFormSubmitLabel: string;
    showCustomerForm: boolean = false;
    showMerchandiserForm: boolean = false;
    showContactSearchDialog: boolean = false;
    contactDialogMode: 'default' | 'access' = 'default';
    contactSearchQuery: string = '';
    isContactSearchLoading: boolean = false;
    isDialogMode = false;
    hasSubmittedLocationForm = false;
    isGoogleMapsUrlExtracting = false;
    googleMapsUrlStatus = '';
    googleMapsUrlError = '';
    private loadedCityCountryId?: number;
    private activeMerchandiserAssignmentIndex: number | null = null;


    constructor(private sessionStorageService: SessionStorageService, private route: ActivatedRoute,
        private readonly googleMapsLibrariesLoaderService: GoogleMapsLibrariesLoaderService,
        private customerService: CustomerService, private router: Router, private http: HttpClient,
        private messageService: MessageService, private companyService: CompanyService,
        private locationService: LocationService, private crmService: CrmService, private permissionService: PermissionService,
        private baseMasterDataService: BaseMasterDataService, private transportPlanningService: TransportPlanningService,
        @Optional() private dialogRef: DynamicDialogRef | null,
        @Optional() private dialogConfig: DynamicDialogConfig | null) {
    }

    ngOnInit(): void {
        this.canCreateLocation = this.permissionService.hasPermission(app_permissions.location.records.create);
        this.canEditLocation = this.permissionService.hasPermission(app_permissions.location.records.edit);
        this.loadGeoMasterData();
        this.loadRetailers();
        this.createEditLocationForm = new FormGroup({
            searchLocation: new FormControl('', Validators.required),
            googleMapsUrl: new FormControl(''),
            searchDefaultContact: new FormControl(''),
            contactsDropdown: new FormControl([]),
            companyDropdown: new FormControl(''),
            countryDropdown: new FormControl('', Validators.required),
            cityDropdown: new FormControl('', Validators.required),
            locationTypeDropdown: new FormControl('', Validators.required),
            locationZoneDropdown: new FormControl(''),
            externalLocationReferenceInput: new FormControl(''),
            nameInput: new FormControl('', Validators.required),
        });
        this.searchLocation = this.createEditLocationForm.get('searchLocation') as FormControl;
        this.googleMapsUrl = this.createEditLocationForm.get('googleMapsUrl') as FormControl;
        this.searchDefaultContact = this.createEditLocationForm.get('searchDefaultContact') as FormControl;
        this.contactsDropdown = this.createEditLocationForm.get('contactsDropdown') as FormControl;
        this.companyDropdown = this.createEditLocationForm.get('companyDropdown') as FormControl;
        this.countryDropdown = this.createEditLocationForm.get('countryDropdown') as FormControl;
        this.cityDropdown = this.createEditLocationForm.get('cityDropdown') as FormControl;
        this.locationTypeDropdown = this.createEditLocationForm.get('locationTypeDropdown') as FormControl;
        this.locationZoneDropdown = this.createEditLocationForm.get('locationZoneDropdown') as FormControl;
        this.externalLocationReferenceInput = this.createEditLocationForm.get('externalLocationReferenceInput') as FormControl;
        this.nameInput = this.createEditLocationForm.get('nameInput') as FormControl;
        this.createContactForm = new FormGroup({
            clientSelectDropdown: new FormControl('', Validators.required),
            contactNameInput: new FormControl('', Validators.required),
            contactPhoneInput: new FormControl('', Validators.required),
        });
        this.customerList = [];
        this.searchContactResults = [];
        this.loadLocationZones();

        // Get location ID from route parameters
        this.isDialogMode = !!this.dialogRef;
        const locationId = this.isDialogMode ? this.dialogConfig?.data?.locationId : this.route.snapshot.paramMap.get('id');
        this.canManageLocation = locationId ? this.canEditLocation : this.canCreateLocation;
        if (!this.canManageLocation) {
            this.createEditLocationForm.disable({ emitEvent: false });
        }

        if (locationId) {
            this.locationId = Number(locationId);
            this.loadLocation(); // Load location if ID exists
        } else {
            this.initializeForNewLocation(); // Initialize for new location
        }
        this.loadCrmAssignmentData();

        this.breadcrumbItems = [{ label: 'Locations', routerLink: '/location' }, { label: locationId ? 'Update' : 'Create' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
    }

    ngAfterViewInit(): void {
        const mapElement = document.getElementById("map") as HTMLElement | null;
        if (mapElement) {
            this.googleMapsLibrariesLoaderService.lazyLoadMap().subscribe(_ =>
                this.googleMap = new google.maps.Map(mapElement));
        }
        if (!navigator.geolocation) {
            console.log('location is not supported');
        }
        navigator.geolocation.getCurrentPosition((position) => {
            //this.orderQuery.searchLocation = new LatLng(position.coords.latitude, position.coords.longitude);
        });
    }

    initializeForNewLocation() {
        // Initialize for a new location (if needed)
        this.createEditLocation = new Location();
        this.createEditLocation.locationType = this.defaultLocationType();
        this.createEditLocation.country = this.sessionStorageService.getCountryList()?.find(country => country.countryCode == 'SA')!!;
        this.createEditLocation.countryId = this.createEditLocation.country?.id;
        this.loadCitiesByCountry(this.createEditLocation.countryId);
        this.locationTypeDropdown?.setValue(this.createEditLocation.locationType);
        this.locationZoneDropdown?.setValue(null);
        this.countryDropdown?.setValue(this.createEditLocation.countryId ?? null);
        if (this.createEditLocation.countryId) {
            this.companyService.getCompaniesByCountry(this.createEditLocation.countryId).subscribe(companies => {
                this.companyList = companies;
            });
        }
    }

    loadLocation() {
        this.isLoading = true;
        this.locationService.getLocationById(this.locationId).subscribe(locationWrapper => {
            console.log(locationWrapper);
            this.createEditLocation = locationWrapper.data[0];
            this.createEditLocation.countryId = this.createEditLocation.countryId ?? this.createEditLocation.country?.id;
            const finishLoad = () => {
                this.updateFormValues();
                this.isLoading = false;
            };
            if (this.createEditLocation.countryId) {
                this.loadCitiesByCountry(this.createEditLocation.countryId, finishLoad);
            } else {
                finishLoad();
            }
            if (this.createEditLocation.countryId) {
                this.companyService.getCompaniesByCountry(this.createEditLocation.countryId).subscribe(companies => {
                    this.companyList = companies;
                });
            }
        });
    }

    updateFormValues() {
        if (!this.createEditLocationForm) {
            return;
        }
        this.createEditLocationForm.patchValue({
            searchLocation: this.createEditLocation.name, // Assuming you want to display the name in the search field
            searchDefaultContact: this.contactLabel(this.createEditLocation.defaultContact),
            contactsDropdown: this.createEditLocation.contacts ?? [],
            companyDropdown: this.createEditLocation.company,
            countryDropdown: this.createEditLocation.countryId,
            cityDropdown: this.createEditLocation.cityId,
            locationTypeDropdown: this.createEditLocation.locationType,
            locationZoneDropdown: this.createEditLocation.locationZoneId,
            externalLocationReferenceInput: this.createEditLocation.externalLocationReference,
            nameInput: this.createEditLocation.name
        });
    }

    /**
     * This method is now called by the (placeSelected) event from the app-location-search component.
     */
    placeSelect(result: google.maps.places.PlaceResult): void {
        if (!result || !result.geometry?.location) return;

        this.createEditLocation.name = result.name ?? '';

        const lat = result.geometry.location.lat();
        const lng = result.geometry.location.lng();
        let geoAddress: GeoAddress = new GeoAddress();
        result.address_components?.forEach(addressComponent => {
            let geoElement = new GeoElement();
            geoElement.name = addressComponent.short_name;
            geoElement.types = addressComponent.types;
            geoAddress.addressElements.push(geoElement);
        });

        this.createEditLocation.location = new LatLng(lat, lng);
        this.createEditLocation.address = geoAddress;
        this.applyCountryCityFromPlace(result, lat, lng);
        this.createEditLocation.telephoneNumber = result.international_phone_number ?? null;
        this.createEditLocation.buildingImage = result.photos?.[0]?.getUrl() ?? null;
        this.createEditLocation.helperImage1 = result.photos?.[1]?.getUrl() ?? null;
        this.createEditLocation.helperImage2 = result.photos?.[2]?.getUrl() ?? null;
        this.createEditLocation.helperImage3 = result.photos?.[3]?.getUrl() ?? null;

        this.patchPlaceFormControls(result.formatted_address);
    }

    setCoordinate(axis: 'latitude' | 'longitude', value: number | null): void {
        this.createEditLocation.location ??= new LatLng(NaN, NaN);
        this.createEditLocation.location[axis] = value == null ? NaN : Number(value);
    }

    hasValidCoordinates(): boolean {
        const { latitude, longitude } = this.createEditLocation.location ?? {};
        return typeof latitude === 'number' && Number.isFinite(latitude) && latitude >= -90 && latitude <= 90
            && typeof longitude === 'number' && Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;
    }

    extractGoogleMapsUrl(): void {
        const url = String(this.googleMapsUrl?.value ?? '').trim();
        this.googleMapsUrlStatus = '';
        this.googleMapsUrlError = '';

        if (!this.isGoogleMapsUrl(url)) {
            this.googleMapsUrlError = 'Paste a valid Google Maps link.';
            return;
        }

        this.isGoogleMapsUrlExtracting = true;
        const extractorUrl = `https://google-maps-url-extractor-113166165184.europe-west1.run.app/?shortUrl=${encodeURIComponent(url)}`;
        this.http.get<{ lat?: string | number; lng?: string | number; fullUrl?: string }>(extractorUrl).subscribe({
            next: response => {
                const coordinates = this.coordinatesFromGoogleMapsResponse(response);
                if (!coordinates) {
                    this.finishGoogleMapsExtraction('Coordinates were not available in this Google Maps link.');
                    return;
                }
                this.prefillLocationSearchCoordinates(coordinates.lat, coordinates.lng);
            },
            error: () => this.finishGoogleMapsExtraction('Could not expand this Google Maps link. Please try another link.')
        });
    }

    private prefillLocationSearchCoordinates(lat: number, lng: number): void {
        const coordinates = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        this.searchLocation.setValue(coordinates);
        this.locationSearchComponent?.searchPlaces({ query: coordinates });
        this.isGoogleMapsUrlExtracting = false;
        this.googleMapsUrlStatus = `Coordinates loaded into Google Places search: ${coordinates}`;
    }

    private finishGoogleMapsExtraction(error: string): void {
        this.isGoogleMapsUrlExtracting = false;
        this.googleMapsUrlError = error;
    }

    private isGoogleMapsUrl(value: string): boolean {
        try {
            const host = new URL(value).hostname.toLowerCase();
            return host === 'maps.app.goo.gl'
                || host === 'www.google.com'
                || host === 'google.com'
                || host === 'maps.google.com';
        } catch {
            return false;
        }
    }

    private coordinatesFromGoogleMapsResponse(response: { lat?: string | number; lng?: string | number; fullUrl?: string }): { lat: number; lng: number } | null {
        const lat = this.validGoogleMapsCoordinate(response.lat);
        const lng = this.validGoogleMapsCoordinate(response.lng);
        if (lat != null && lng != null) {
            return { lat, lng };
        }

        const fullUrl = response.fullUrl;
        if (!fullUrl) {
            return null;
        }

        let decodedUrl = fullUrl;
        try {
            decodedUrl = decodeURIComponent(fullUrl);
        } catch {
            // The coordinate patterns can be read from an un-decoded expanded URL too.
        }

        const coordinateMatch = decodedUrl.match(/\/place\/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/i)
            ?? decodedUrl.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/i);
        if (!coordinateMatch) {
            return null;
        }

        return {
            lat: Number(coordinateMatch[1]),
            lng: Number(coordinateMatch[2])
        };
    }

    private validGoogleMapsCoordinate(value: unknown): number | null {
        if (value == null || (typeof value === 'string' && !value.trim())) {
            return null;
        }
        const coordinate = Number(value);
        return Number.isFinite(coordinate) ? coordinate : null;
    }


    openContactSearchDialog(mode: 'default' | 'access' = 'default'): void {
        this.contactDialogMode = mode;
        this.showContactSearchDialog = true;
        if (!this.contactSearchQuery && mode === 'default' && this.createEditLocation.defaultContact) {
            this.contactSearchQuery = this.contactLabel(this.createEditLocation.defaultContact);
        }
        if (this.contactSearchQuery.trim().length >= 2) {
            this.searchContacts();
        }
    }

    searchContacts(): void {
        const query = this.contactSearchQuery.trim();
        if (query.length < 2) {
            this.searchContactResults = [];
            return;
        }

        this.isContactSearchLoading = true;
        this.customerService.searchCustomers(query, 0, 25).subscribe({
            next: contacts => {
                this.searchContactResults = contacts ?? [];
                this.customerList = this.searchContactResults;
                this.isContactSearchLoading = false;
            },
            error: error => {
                console.error('Error searching contacts:', error);
                this.searchContactResults = [];
                this.isContactSearchLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Contact search failed',
                    detail: 'Could not search contacts. Please try again.'
                });
            }
        });
    }

    selectContactFromDialog(contact: Customer): void {
        if (this.contactDialogMode === 'access') {
            this.addLocationContact(contact);
        } else {
            this.selectDefaultContact(contact);
        }
        this.showContactSearchDialog = false;
    }

    onContactCleared(): void {
        this.createEditLocation.defaultContact = undefined as any;
        this.searchDefaultContact.setValue(null);
    }

    createUpdateLocation() {
        if (!this.canManageLocation) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to save this location.' });
            return;
        }
        this.hasSubmittedLocationForm = true;
        this.createEditLocationForm.markAllAsTouched();
        this.validateLocationBeforeSave();
        if (this.errorMessages.length > 0) {
            console.log(this.errorMessages);
            return;
        }
        this.locationCreationInProgress = true;
        if (!this.createEditLocation.id) {
            this.createEditLocation.managedBy = this.sessionStorageService.getCustomer()!!.company;
        }
        this.createEditLocation.country = this.countryList.find(country => country.id === this.createEditLocation.countryId) ?? this.createEditLocation.country;
        if (this.createEditLocation.id) {
            this.locationService.updateLocation(this.createEditLocation).subscribe(createLocationResponse => {
                this.locationCreationInProgress = false;
                if (createLocationResponse.success) {
                    this.handleSaveSuccess(createLocationResponse.data, 'updated');
                }
            }, error => {
                this.locationCreationInProgress = false;
                this.messageService.add({
                    severity: 'error', summary: 'Location ' + this.createEditLocation.name + ' is NOT created',
                    detail: 'Location ' + this.createEditLocation.name + ' is not created: ' + JSON.stringify(error)
                });
            });
        } else {
            this.locationService.createLocation(this.createEditLocation).subscribe(createLocationResponse => {
                this.locationCreationInProgress = false;
                if (createLocationResponse.success) {
                    this.handleSaveSuccess(createLocationResponse.data, 'created');
                }
            }, error => {
                this.locationCreationInProgress = false;
                this.messageService.add({
                    severity: 'error', summary: 'Location ' + this.createEditLocation.name + ' is NOT created',
                    detail: 'Location ' + this.createEditLocation.name + ' is not created: ' + JSON.stringify(error)
                });
            });
        }
    }

    clearForm() {
        this.errorMessages = '';
        this.errorMessageList = [];
        this.hasSubmittedLocationForm = false;
        if (this.locationId) {
            this.loadLocation();
            return;
        }
        this.createEditLocationForm.reset();
        this.initializeForNewLocation();
    }

    cancel() {
        if (this.isDialogMode && this.dialogRef) {
            this.dialogRef.close();
            return;
        }
        this.router.navigate(['/location']);
    }

    private handleSaveSuccess(savedLocation: Location, action: 'created' | 'updated') {
        this.messageService.add({
            severity: 'success',
            summary: 'Location ' + savedLocation.name + ' is ' + action,
            detail: 'Location ' + savedLocation.name + ' has been successfully ' + action
        });
        if (this.isDialogMode && this.dialogRef) {
            this.dialogRef.close(savedLocation);
            return;
        }
        this.router.navigate(['/location']);
    }

    registerCompany() {
        this.companyFormHeader = 'Create New Company';
        this.companyFormSubmitLabel = 'Create';
        this.selectedCustomer = new Customer(); // Clear for new customer
        this.selectedCompany = new Company(); // Clear for new company
        this.showCompanyForm = true;
    }

    registerContact(searchText: string = '') {
        const seedText = searchText.trim();
        this.customerFormHeader = 'Create New Contact';
        this.customerFormSubmitLabel = 'Create';
        this.selectedCustomer = new Customer(); // Clear for new customer
        if (/^\+?\d[\d\s-]{6,}$/.test(seedText)) {
            this.selectedCustomer.phoneNumber = seedText.replace(/\s+/g, '');
        } else if (seedText.includes('@')) {
            this.selectedCustomer.emailAddress = seedText;
        } else if (seedText) {
            this.selectedCustomer.name = seedText;
        }
        this.selectedCompany = new Company();
        this.showContactSearchDialog = false;
        this.showCustomerForm = true;
    }

    registerDefaultContact(): void {
        this.registerContact(this.contactSearchQuery);
    }

    onCustomerFormClosed(result?: Customer | boolean) {
        this.showCustomerForm = false;
        if (result === false) {
            return;
        }

        const savedContact = this.isCustomerResult(result) ? result : undefined;
        if (savedContact) {
            this.customerList = this.upsertById(this.customerList ?? [], savedContact);
            this.searchContactResults = [...this.customerList];
            if (this.contactDialogMode === 'access') {
                this.addLocationContact(savedContact);
            } else {
                this.selectDefaultContact(savedContact);
            }
        }
    }

    onCompanyFormClosed(result?: Company | boolean) {
        this.showCompanyForm = false;
        if (result === false) {
            return;
        }

        const savedCompany = this.isCompanyResult(result) ? result : undefined;
        const countryId = savedCompany?.countryId
            ?? this.createEditLocation.countryId
            ?? this.sessionStorageService.getCountryList()?.find(country => country.countryCode == 'SA')?.id;
        if (!countryId) {
            return;
        }

        if (savedCompany) {
            this.companyList = this.upsertById(this.companyList ?? [], savedCompany);
            this.selectParentCompany(savedCompany);
        }

        this.companyService.getCompaniesByCountry(countryId, true).subscribe(companies => {
            this.companyList = savedCompany ? this.upsertById(companies ?? [], savedCompany) : (companies ?? []);
            const selectedCompany = savedCompany
                ? this.companyList.find(company => company.id === savedCompany.id) ?? savedCompany
                : this.latestById(this.companyList);
            if (selectedCompany) {
                this.selectParentCompany(selectedCompany);
            }
        });
    }

    shouldShowFieldError(controlName: string): boolean {
        const control = this.createEditLocationForm.get(controlName);
        return !!control?.invalid && (control.touched || this.hasSubmittedLocationForm);
    }

    shouldShowLocationCoordinatesError(): boolean {
        return this.hasSubmittedLocationForm && !this.hasValidCoordinates();
    }

    onCountrySelected(countryId: number | null): void {
        this.createEditLocation.countryId = countryId ?? undefined;
        this.createEditLocation.country = this.countryList.find(country => country.id === countryId) as Country;
        const clearCityIfMissing = () => {
            if (!this.filteredCityList.some(city => city.id === this.createEditLocation.cityId)) {
                this.createEditLocation.cityId = undefined;
                this.cityDropdown.setValue(null);
            }
        };
        if (this.createEditLocation.countryId) {
            this.loadCitiesByCountry(this.createEditLocation.countryId, clearCityIfMissing);
        } else {
            this.cityList = [];
            this.filteredCityList = [];
            this.loadedCityCountryId = undefined;
            this.createEditLocation.cityId = undefined;
            this.cityDropdown.setValue(null);
        }
        if (this.createEditLocation.countryId) {
            this.companyService.getCompaniesByCountry(this.createEditLocation.countryId).subscribe(companies => {
                this.companyList = companies;
            });
        }
    }

    onCompanySelected(company: Company | null): void {
        this.createEditLocation.company = company as Company;
    }

    private loadRetailers(): void {
        const owningCompanyId = this.sessionStorageService.getCompany()?.id
            ?? this.sessionStorageService.getCustomer()?.company?.id;
        if (!owningCompanyId) {
            this.retailerList = [];
            return;
        }
        this.locationService.getRetailers(owningCompanyId).subscribe({
            next: response => this.retailerList = response?.data ?? [],
            error: () => this.retailerList = []
        });
    }

    onCitySelected(cityId: number | null): void {
        this.createEditLocation.cityId = cityId ?? undefined;
        const city = this.cityList.find(value => value.id === cityId);
        if (city?.country?.id && city.country.id !== this.createEditLocation.countryId) {
            this.createEditLocation.countryId = city.country.id;
            this.createEditLocation.country = city.country;
            this.countryDropdown.setValue(city.country.id);
            this.filterCitiesByCountry(city.country.id);
        }
    }

    private loadGeoMasterData(): void {
        this.countryList = this.sessionStorageService.getCountryList() ?? [];
    }

    private loadLocationZones(): void {
        const companyId = this.sessionStorageService.getCompany()?.id
            ?? this.sessionStorageService.getCustomer()?.company?.id;
        if (!companyId) {
            this.locationZones = [];
            return;
        }
        this.transportPlanningService.getLocationZones(companyId, true).subscribe({
            next: zones => {
                this.locationZones = zones ?? [];
                const selectedId = this.createEditLocation?.locationZoneId;
                if (selectedId && !this.locationZones.some(zone => zone.id === selectedId)) {
                    this.createEditLocation.locationZoneId = undefined;
                    this.locationZoneDropdown?.setValue(null);
                }
            },
            error: () => {
                this.locationZones = [];
                this.messageService.add({
                    severity: 'error',
                    summary: 'Location zones unavailable',
                    detail: 'Active location zones could not be loaded.'
                });
            }
        });
    }

    private loadCitiesByCountry(countryId?: number, afterLoad?: () => void): void {
        if (!countryId) {
            this.cityList = [];
            this.filteredCityList = [];
            this.loadedCityCountryId = undefined;
            afterLoad?.();
            return;
        }

        if (this.loadedCityCountryId === countryId && this.cityList.length > 0) {
            this.filterCitiesByCountry(countryId);
            afterLoad?.();
            return;
        }

        this.baseMasterDataService.getCities('en-US', countryId).subscribe({
            next: cities => {
                this.loadedCityCountryId = countryId;
                this.cityList = (cities ?? []).map(city => this.normalizeCityOption(city));
                this.filterCitiesByCountry(countryId);
                afterLoad?.();
            },
            error: error => {
                console.error('Error loading cities:', error);
                this.cityList = [];
                this.filteredCityList = [];
                this.loadedCityCountryId = undefined;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Cities unavailable',
                    detail: 'Could not load cities for the selected country.'
                });
                afterLoad?.();
            }
        });
    }

    private filterCitiesByCountry(countryId?: number): void {
        if (!countryId) {
            this.filteredCityList = [];
            return;
        }

        this.filteredCityList = this.loadedCityCountryId === countryId
            ? [...this.cityList]
            : this.cityList.filter(city => city.country?.id === countryId);
    }

    private loadCrmAssignmentData(): void {
        const companyId = this.sessionStorageService.getCompany()?.id ?? this.sessionStorageService.getCustomer()?.company?.id;
        if (!companyId) {
            this.merchandiserList = [];
            this.crmClientList = [];
            this.crmAccountList = [];
            return;
        }
        this.crmService.getMerchandisers(companyId).subscribe({
            next: merchandisers => {
                this.merchandiserList = merchandisers ?? [];
                this.syncAssignmentReferences();
            },
            error: error => {
                console.error('Error loading merchandisers:', error);
                this.merchandiserList = [];
            }
        });
        this.crmService.getClients(companyId).subscribe({
            next: clients => {
                this.crmClientList = clients ?? [];
                this.syncAssignmentReferences();
            },
            error: error => {
                console.error('Error loading CRM clients:', error);
                this.crmClientList = [];
            }
        });
        this.crmService.getAccounts(companyId).subscribe({
            next: accounts => {
                this.crmAccountList = accounts ?? [];
                this.syncAssignmentReferences();
            },
            error: error => {
                console.error('Error loading CRM accounts:', error);
                this.crmAccountList = [];
            }
        });
    }

    contactLabel(contact?: Customer | null): string {
        if (!contact) {
            return '';
        }
        return contact.name || contact.phoneNumber || contact.emailAddress || `Contact #${contact.id}`;
    }

    contactMeta(contact?: Customer | null): string {
        if (!contact) {
            return '';
        }
        return [contact.phoneNumber, contact.emailAddress].filter(Boolean).join(' | ');
    }

    merchandiserLabel(merchandiser?: CrmMerchandiser | null): string {
        if (!merchandiser) {
            return '';
        }
        return merchandiser.name || merchandiser.merchandiserNumber || `Merchandiser #${merchandiser.id}`;
    }

    merchandiserMeta(merchandiser?: CrmMerchandiser | null): string {
        if (!merchandiser) {
            return '';
        }
        return [merchandiser.mobileNumber || merchandiser.phoneNumber, merchandiser.emailAddress]
            .filter(Boolean)
            .join(' | ');
    }

    openMerchandiserDialog(index: number): void {
        const assignment = this.assignmentRows()[index];
        if (!assignment?.crmClientId || !assignment?.crmAccountId) {
            this.messageService.add({
                severity: 'warn',
                summary: 'CRM mapping required',
                detail: 'Select CRM client and CRM account before creating a merchandiser.'
            });
            return;
        }
        this.activeMerchandiserAssignmentIndex = index;
        this.showMerchandiserForm = true;
    }

    closeMerchandiserDialog(): void {
        this.showMerchandiserForm = false;
        this.activeMerchandiserAssignmentIndex = null;
    }

    onMerchandiserSaved(merchandiser: CrmMerchandiser): void {
        if (!merchandiser?.id) {
            this.closeMerchandiserDialog();
            return;
        }
        const assignment = this.activeMerchandiserAssignment();
        this.merchandiserList = this.upsertById(this.merchandiserList ?? [], merchandiser);
        if (assignment) {
            assignment.merchandiserId = merchandiser.id;
            assignment.merchandiser = merchandiser;
            this.onAssignmentMerchandiserChange(assignment);
        }
        this.closeMerchandiserDialog();
    }

    activeMerchandiserClientName(): string | null {
        const assignment = this.activeMerchandiserAssignment();
        return assignment?.crmClientId
            ? this.crmClientList.find(client => client.id === assignment.crmClientId)?.companyName || null
            : null;
    }

    activeMerchandiserAccountName(): string | null {
        const assignment = this.activeMerchandiserAssignment();
        return assignment?.crmAccountId
            ? this.crmAccountList.find(account => account.id === assignment.crmAccountId)?.companyName || null
            : null;
    }

    merchandiserDialogCompanyId(): number | null {
        return this.sessionStorageService.getCompany()?.id
            ?? this.sessionStorageService.getCustomer()?.company?.id
            ?? null;
    }

    private defaultLocationType(): string {
        const context = this.locationCreationSourceContext();
        if (context === 'WAREHOUSE') {
            return 'WAREHOUSE';
        }
        if (context === 'POSM_STORE') {
            return 'CUSTOMER';
        }
        return 'CUSTOMER';
    }

    private locationCreationSourceContext(): LocationCreationSourceContext {
        const context = String(
            this.dialogConfig?.data?.sourceContext
            ?? this.route.snapshot.queryParamMap.get('sourceContext')
            ?? 'GENERAL'
        ).toUpperCase();
        if (context === 'WAREHOUSE' || context === 'POSM_STORE') {
            return context;
        }
        return 'GENERAL';
    }

    cityLabel(city?: City | null): string {
        if (!city) {
            return '';
        }
        return city.cityName || `City #${city.id}`;
    }

    private selectParentCompany(company: Company): void {
        this.createEditLocation.company = company;
        this.companyDropdown.setValue(company);
    }

    private selectDefaultContact(contact: Customer): void {
        this.createEditLocation.defaultContact = contact;
        this.searchDefaultContact.setValue(this.contactLabel(contact));
        this.addLocationContact(contact);
    }

    private addLocationContact(contact: Customer): void {
        if (!contact?.id) {
            return;
        }
        this.customerList = this.upsertById(this.customerList ?? [], contact);
        this.createEditLocation.contacts = this.upsertById(this.createEditLocation.contacts ?? [], contact);
        this.contactsDropdown.setValue(this.createEditLocation.contacts);
    }

    private latestById<T extends { id?: number }>(items: T[] = []): T | undefined {
        return [...items]
            .filter(item => item?.id != null)
            .sort((left, right) => (left.id ?? 0) - (right.id ?? 0))
            .pop();
    }

    private upsertById<T extends { id?: number }>(items: T[] = [], item: T): T[] {
        if (!item?.id) {
            return items;
        }
        return [...items.filter(existing => existing.id !== item.id), item];
    }

    private isCompanyResult(result?: Company | boolean): result is Company {
        return typeof result === 'object' && result !== null && !!result.id;
    }

    private isCustomerResult(result?: Customer | boolean): result is Customer {
        return typeof result === 'object' && result !== null && !!result.id;
    }

    private normalizeCityOption(city: City): CityOption {
        const rawCity = city as CityOption & Record<string, unknown>;
        const displayName = [
            rawCity.cityName,
            rawCity['name'],
            rawCity['city'],
            rawCity['city_name'],
            rawCity['displayName']
        ].find(value => typeof value === 'string' && value.trim().length > 0) as string | undefined;
        const cityName = displayName ?? (rawCity.id ? `City #${rawCity.id}` : 'Unnamed city');
        return {
            ...city,
            cityName,
            searchText: [cityName, city.country?.name, city.country?.countryCode].filter(Boolean).join(' ')
        };
    }

    private applyCountryCityFromPlace(result: google.maps.places.PlaceResult, latitude: number, longitude: number): void {
        const matchedCountry = this.findCountryFromPlace(result)
            ?? this.createEditLocation.country;
        if (matchedCountry) {
            this.createEditLocation.country = matchedCountry;
            this.createEditLocation.countryId = matchedCountry.id;
            if (this.loadedCityCountryId !== matchedCountry.id) {
                this.loadCitiesByCountry(matchedCountry.id, () => {
                    this.applyCityFromPlace(result, latitude, longitude, matchedCountry);
                    this.patchPlaceFormControls(result.formatted_address);
                });
                return;
            }
            this.filterCitiesByCountry(matchedCountry.id);
        }

        this.applyCityFromPlace(result, latitude, longitude, matchedCountry);
    }

    private applyCityFromPlace(
        result: google.maps.places.PlaceResult,
        latitude: number,
        longitude: number,
        matchedCountry?: Country
    ): void {
        const candidateCities = this.createEditLocation.countryId ? this.filteredCityList : this.cityList;
        const matchedCity = this.findCityFromPlace(result, candidateCities)
            ?? this.nearestCity(candidateCities, latitude, longitude);
        if (matchedCity) {
            this.createEditLocation.cityId = matchedCity.id;
            if (matchedCity.country && matchedCity.country.id !== this.createEditLocation.countryId) {
                this.createEditLocation.country = matchedCity.country;
                this.createEditLocation.countryId = matchedCity.country.id;
                this.filterCitiesByCountry(matchedCity.country.id);
            }
            return;
        }

        if (matchedCountry && !this.filteredCityList.some(city => city.id === this.createEditLocation.cityId)) {
            this.createEditLocation.cityId = undefined;
        }
    }

    private patchPlaceFormControls(searchLocation?: string): void {
        if (!this.createEditLocationForm) {
            return;
        }
        this.createEditLocationForm.patchValue({
            searchLocation: searchLocation,
            nameInput: this.createEditLocation.name,
            countryDropdown: this.createEditLocation.countryId,
            cityDropdown: this.createEditLocation.cityId
        });
    }

    private findCountryFromPlace(result: google.maps.places.PlaceResult): Country | undefined {
        const countryComponent = this.addressComponent(result, 'country');
        const countryCode = this.normalizePlaceText(countryComponent?.short_name);
        const countryName = this.normalizePlaceText(countryComponent?.long_name);
        return this.countryList.find(country =>
            this.normalizePlaceText(country.countryCode) === countryCode
            || this.normalizePlaceText(country.name) === countryName
            || this.normalizePlaceText(country.name) === countryCode);
    }

    private findCityFromPlace(result: google.maps.places.PlaceResult, candidateCities: City[]): City | undefined {
        const placeCityNames = this.cityNameCandidatesFromPlace(result)
            .map(value => this.normalizePlaceText(value))
            .filter(Boolean);
        if (!placeCityNames.length) {
            return undefined;
        }

        const cityMatches = candidateCities
            .map(city => ({ city, normalizedName: this.normalizePlaceText(city.cityName) }))
            .filter(match => !!match.normalizedName);
        return cityMatches.find(match => placeCityNames.includes(match.normalizedName))?.city
            ?? cityMatches.find(match => placeCityNames.some(placeName =>
                placeName.length > 3
                && match.normalizedName.length > 3
                && (placeName.includes(match.normalizedName) || match.normalizedName.includes(placeName))))?.city;
    }

    private cityNameCandidatesFromPlace(result: google.maps.places.PlaceResult): string[] {
        const cityComponentTypes = [
            'locality',
            'postal_town',
            'administrative_area_level_3',
            'administrative_area_level_2',
            'administrative_area_level_1',
            'sublocality',
            'sublocality_level_1'
        ];
        return cityComponentTypes.flatMap(type =>
            this.addressComponents(result, type)
                .flatMap(component => [component.long_name, component.short_name])
                .filter(Boolean) as string[]);
    }

    private addressComponent(result: google.maps.places.PlaceResult, type: string): google.maps.GeocoderAddressComponent | undefined {
        return result.address_components?.find(component => component.types.includes(type));
    }

    private addressComponents(result: google.maps.places.PlaceResult, type: string): google.maps.GeocoderAddressComponent[] {
        return result.address_components?.filter(component => component.types.includes(type)) ?? [];
    }

    private normalizePlaceText(value?: string | null): string {
        return String(value ?? '')
            .normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/\b(city|province|region|governorate|municipality)\b/g, ' ')
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    }

    private nearestCity(cities: City[], latitude: number, longitude: number): City | undefined {
        return cities
            .filter(city => city.coordinates?.latitude != null && city.coordinates?.longitude != null)
            .sort((left, right) =>
                this.distanceSquared(left, latitude, longitude) - this.distanceSquared(right, latitude, longitude))[0];
    }

    private distanceSquared(city: City, latitude: number, longitude: number): number {
        return Math.pow((city.coordinates?.latitude ?? latitude) - latitude, 2)
            + Math.pow((city.coordinates?.longitude ?? longitude) - longitude, 2);
    }

    validateLocationBeforeSave() {
        this.errorMessages = '';
        if (!this.createEditLocation.name)
            this.errorMessages += 'Location name is required. ';
        if (!this.createEditLocation.locationType)
            this.errorMessages += 'Location type is required. ';
        if (!this.createEditLocation.countryId)
            this.errorMessages += 'Country is required. ';
        if (!this.createEditLocation.cityId)
            this.errorMessages += 'City is required. ';
        if (!this.hasValidCoordinates())
            this.errorMessages += 'Valid latitude and longitude are required. ';
        if ((this.createEditLocation.merchandiserAssignments ?? []).some(assignment =>
            !assignment.crmClientId || !assignment.crmAccountId || !assignment.merchandiserId)) {
            this.errorMessages += 'Each merchandiser mapping requires CRM client, CRM account, and merchandiser. ';
        }
    }

    assignmentRows(): LocationMerchandiserAssignment[] {
        if (!this.createEditLocation.merchandiserAssignments?.length && this.createEditLocation.merchandisers?.length) {
            this.createEditLocation.merchandiserAssignments = this.createEditLocation.merchandisers.map(merchandiser => ({
                merchandiserId: merchandiser.id,
                merchandiser,
                active: true
            }));
        }
        return this.createEditLocation.merchandiserAssignments ?? [];
    }

    addMerchandiserAssignment(): void {
        this.createEditLocation.merchandiserAssignments = [
            ...(this.createEditLocation.merchandiserAssignments ?? []),
            { active: true }
        ];
    }

    removeMerchandiserAssignment(index: number): void {
        const rows = [...(this.createEditLocation.merchandiserAssignments ?? [])];
        rows.splice(index, 1);
        this.createEditLocation.merchandiserAssignments = rows;
        this.refreshDerivedMerchandisers();
    }

    accountsForClient(crmClientId?: number): CrmAccount[] {
        return (this.crmAccountList ?? []).filter(account => !crmClientId || account.crmClientId === crmClientId);
    }

    onAssignmentClientChange(assignment: LocationMerchandiserAssignment): void {
        assignment.crmClient = this.crmClientList.find(client => client.id === assignment.crmClientId);
        if (!this.accountsForClient(assignment.crmClientId).some(account => account.id === assignment.crmAccountId)) {
            assignment.crmAccountId = undefined;
            assignment.crmAccount = undefined;
        }
    }

    onAssignmentAccountChange(assignment: LocationMerchandiserAssignment): void {
        assignment.crmAccount = this.crmAccountList.find(account => account.id === assignment.crmAccountId);
    }

    onAssignmentMerchandiserChange(assignment: LocationMerchandiserAssignment): void {
        assignment.merchandiser = this.merchandiserList.find(merchandiser => merchandiser.id === assignment.merchandiserId);
        this.refreshDerivedMerchandisers();
    }

    private activeMerchandiserAssignment(): LocationMerchandiserAssignment | null {
        if (this.activeMerchandiserAssignmentIndex == null) {
            return null;
        }
        return this.assignmentRows()[this.activeMerchandiserAssignmentIndex] ?? null;
    }

    private syncAssignmentReferences(): void {
        const rows = this.assignmentRows();
        for (const assignment of rows) {
            if (assignment.crmClientId) {
                assignment.crmClient = this.crmClientList.find(client => client.id === assignment.crmClientId) ?? assignment.crmClient;
            }
            if (assignment.crmAccountId) {
                assignment.crmAccount = this.crmAccountList.find(account => account.id === assignment.crmAccountId) ?? assignment.crmAccount;
            }
            if (assignment.merchandiserId) {
                assignment.merchandiser = this.merchandiserList.find(merchandiser => merchandiser.id === assignment.merchandiserId) ?? assignment.merchandiser;
            }
        }
        this.refreshDerivedMerchandisers();
    }

    private refreshDerivedMerchandisers(): void {
        const byId = new Map<number, CrmMerchandiser>();
        for (const assignment of this.createEditLocation.merchandiserAssignments ?? []) {
            if (assignment.crmClient?.id) {
                assignment.crmClientId = assignment.crmClient.id;
            }
            if (assignment.crmAccount?.id) {
                assignment.crmAccountId = assignment.crmAccount.id;
            }
            const merchandiser = assignment.merchandiser
                ?? this.merchandiserList.find(candidate => candidate.id === assignment.merchandiserId);
            if (merchandiser?.id) {
                assignment.merchandiserId = merchandiser.id;
                assignment.merchandiser = merchandiser;
                byId.set(merchandiser.id, merchandiser);
            }
        }
        this.createEditLocation.merchandisers = Array.from(byId.values());
    }

}
